import React, { useState } from 'react';

import axiosInstance from '../axiosInstance';
import {
  Box, 
  Button,
  Typography,
  Paper, Grid,
  CircularProgress,
  LinearProgress, 
  Dialog,  
  DialogTitle,  
  DialogContent,  
  DialogContentText,  
  DialogActions,
  TextField
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { performLogout } from '../utils/logout';

const pageFromMarker = (m) => {
  if (!m || typeof m !== 'string') return null;
  const g = m.match(/---\s*Page\s*(\d+)\s*---/i);
  return g ? Number(g[1]) : null;
};

const endIsNullMarker = (m) => {
  return typeof m === 'string' && m.trim().toLowerCase() === '--- null ---';
};

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

// Build ranges from backend markers WITHOUT forcing adjacency or de-overlap
export default function DocumentAgent() {
  const [iframeKey, setIframeKey] = useState(Date.now());
  const [paidFile, setPaidFile] = useState(null);
  const [paidUploadProgress, setPaidUploadProgress] = useState(0);
  const [paidLoading, setPaidLoading] = useState(false);
  const [paidAiResponse, setPaidAiResponse] = useState('');
  const [paidErrorMsg, setPaidErrorMsg] = useState('');
  const [friendlyClassification, setFriendlyClassification] = useState(null);
  const [rawText, setRawText] = useState('');
  const [numPages, setNumPages] = useState(0);
  const [inputTokens, setInputTokens] = useState(0);
  const [outputTokens, setOutputTokens] = useState(0);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [showClassificationPopup, setShowClassificationPopup] = useState(false);
  const [paidExtractedText, setPaidExtractedText] = useState('');
  const [markerList, setMarkerList] = useState([]);         // backend markers
  const [rangeList, setRangeList] = useState([]);           // UI ranges
  const [downloadFilename, setDownloadFilename] = useState("AI_Analysis_Report.pdf");
  const [confirmedClassification, setConfirmedClassification] = useState([]);
  const [confirmDialogContent, setConfirmDialogContent] = useState('');
  const [pageCount, setPageCount] = useState(0); // used consistently across page-level checks

  // Use wdHideHeaders=True to remove the row/column labels
  // Do NOT include wdHideGridlines, so gridlines remain visible
  const account = useSelector(state => state?.account);

  const excelFileId = account?.user?.doc_agent_excel_name;
  const docAgentExcelUrl = account?.user?.doc_agent_excel_url;

  const baseSharepointUrl = docAgentExcelUrl
    ? docAgentExcelUrl +
      '?e=ccgKwP' +
      '&action=embedview' +
      '&AllowTyping=True' +
      '&wdDownloadButton=True' +
      '&wdInConfigurator=True' +
      '&wdHideHeaders=True'
    : null;


  const dispatch = useDispatch();
  const history = useHistory();
  const orgId = useSelector(state => state?.account?.org?.id || state?.account?.org_data?.data?.id);
  const userId = useSelector(state => state?.account?.user_id || state?.account?.org_member?.id);
  const advancedLicense = account?.user?.licenses_for_advanced;
  


  // ───── Handlers ─────
  const handlePaidFileChange = async (e) => {
    const file = e.target.files[0] || null;
    setPaidFile(file);
    setPaidExtractedText('');
    setPaidAiResponse('');
    setPaidErrorMsg('');
    setPaidUploadProgress(0);
    try {
      //await axiosInstance.post('https://api.projectivetech.com/user/clear_excel_data/', {
      await axiosInstance.post('http://localhost:8000/user/clear_excel_data/', {
        file_id: excelFileId
      });
      setIframeKey(Date.now());
    } catch (err) {
      console.error("Failed to clear Excel:", err);
    }
  };

  const showValidationConfirmation = (friendly_classification) => {
    const message = `Detected document structure:\n\n${friendly_classification
      .map((c) => `${c.doc_type} (Page ${c.page || 'unknown'})`)
      .join("\n")}\n\nProceed?`;

    setConfirmDialogContent(message);
    setConfirmedClassification(friendly_classification);
    setShowClassificationPopup(true);  // ✅ This shows the UI
  }; // ✅ Missing this closing brace


  const handlePaidSubmit = async () => {
    if (!paidFile) {
      setPaidErrorMsg('Please select a file first.');
      return;
    }
    setPaidLoading(true);
    const formData = new FormData();
    formData.append('file', paidFile);
    formData.append("orgId", orgId);
    formData.append("userId", userId);
    formData.append("licenseType",advancedLicense)

    try {
      const response = await axiosInstance.post(
        'https://api.projectivetech.com/user/classify_document',
        //'http://localhost:8000/document_agent/classify_document',
        formData
      );

      const { friendly_classification, raw_text, num_pages, input_tokens, output_tokens } = response.data;
      if (advancedLicense !=0 && num_pages > 10) {
        // ✅ New behavior: advanced license + >10 pages
        setRangeList(Array.isArray(friendly_classification) ? friendly_classification : []);
        setRawText(raw_text || '');
        setNumPages(num_pages);
        setInputTokens(Number(input_tokens || 0));
        setOutputTokens(Number(output_tokens || 0));
        setShowClassificationPopup(true);
      } else {
        // ✅ Old behavior: everything else
        setFriendlyClassification(friendly_classification || []);
        setRawText(raw_text || '');
        setNumPages(num_pages);
        setAwaitingConfirmation(true);
        setInputTokens(Number(input_tokens || 0));
        setOutputTokens(Number(output_tokens || 0));

        showValidationConfirmation(friendly_classification || []);
      }

    } catch (err) {
      console.error(err);
      setPaidErrorMsg('Failed during document classification.');
    }
  };
  
  const updateRange = (index, field, value) => {
    if (!(advancedLicense == 0)) return; // Only allow in advanced mode with >10 pages
    const max = numPages || 1;
    const n = Number(value);
    const v = Number.isFinite(n) ? clamp(n, 1, max) : 1;

    setRangeList((prev) => {
      const next = prev.map((r) => ({ ...r }));
      next[index][field] = v;

      const s = Number(next[index].start) || 1;
      const e = Number(next[index].end) || s;
      if (e < s) {
        if (field === 'start') next[index].end = v;
        else next[index].start = v;
      }
      return next;
    });
  };

  const updateDocType = (index, value) => {
    if (!(advancedLicense == 0 )) return;
    setRangeList((prev) => {
      const next = prev.map((r) => ({ ...r }));
      next[index].doc_type = value || 'Unknown';
      return next;
    });
  };

  const addRange = () => {
    if (!(advancedLicense == 0 )) return;
    const max = numPages || 1;
    const last = rangeList[rangeList.length - 1];
    const start = last ? clamp((Number(last.end) || 1) + 1, 1, max) : 1;
    const end = start;
    setRangeList((prev) => [...prev, { doc_type: 'Unknown', start, end }]);
  };

  const removeRange = (index) => {
    if (!(advancedLicense == 0)) return;
    setRangeList((prev) => prev.filter((_, i) => i !== index));
  };



  const handleFinalValidation = async (confirmedClassification) => {
    const finalFormData = new FormData();
    finalFormData.append('orgId', orgId);
    finalFormData.append('userId', userId);
    finalFormData.append('friendly_classification', JSON.stringify(confirmedClassification));
    finalFormData.append('fileId', excelFileId);
    finalFormData.append('num_pages', numPages);
    finalFormData.append('raw_text', rawText);
    finalFormData.append('input_tokens', inputTokens);
    finalFormData.append('output_tokens', outputTokens);
    finalFormData.append("licenseType",advancedLicense);

    try {
      const response = await axiosInstance.post(
        'https://api.projectivetech.com/user/validate_document_agent',
        //'http://localhost:8000/document_agent/validate_document_agent',
        finalFormData
      );
      const filename = response.data.download_filename || "AI_Analysis_Report.pdf";
      setDownloadFilename(filename);  // ✅ Store filename in state for use in download
      
      const { aiAnswer, SpreadsheetData } = response.data;
      setPaidAiResponse(aiAnswer || '');

      if (SpreadsheetData?.updated_excel?.startsWith('✅')) {
        setIframeKey(Date.now());
      }
    } catch (err) {
      console.error(err);
      setPaidErrorMsg("Final validation failed.");
    } finally {
      setPaidLoading(false);
      setAwaitingConfirmation(false);
    }
  };


  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `https://api.projectivetech.com/user/download_analysis_pdf?filename=${encodeURIComponent(downloadFilename)}`,
        //`http://localhost:8000/document_agent/download_analysis_pdf?filename=${encodeURIComponent(downloadFilename)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error("File not found or download failed.");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
      alert("Download failed. Please try again.");
    }
  };



  const handleLogout = async () => {
    try {
      await axiosInstance.post('https://api.projectivetech.com/user/clear_excel_data/', {
      //await axiosInstance.post('http://localhost:8000/user/clear_excel_data/', {
        file_id: excelFileId,
      });
      setIframeKey(Date.now());
    } catch (err) {
      console.error("Failed to clear Excel:", err);
    }

    performLogout(dispatch, history);
  };

    // ───── Render ─────
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh', overflow: 'auto' }}>
        {/* ───── Overlayed Logout Button ───── */}
       
        <Grid container spacing={2} sx={{ height: '85vh' }}>
          {/* ───── LEFT: Editable Excel Embed (50%) ───── */}
          <Grid item xs={12} md={6} sx={{ height: '100%' }}>
            <Paper
              elevation={3}
              sx={{
                height: '85vh',
                p: 1,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  border: '1px solid #ddd',
                  padding: 1,
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <iframe
                  key={iframeKey}
                  title="Embedded Excel"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  scrolling="no"
                  src={`${baseSharepointUrl}&timestamp=${iframeKey}`}
                  style={{ display: 'block' }}
                />
              </Box>
              {/* (No refresh button—iframe reloads automatically when `iframeKey` changes) */}
            </Paper>
          </Grid>
 
          {/* ───── RIGHT: Document Agent (50%) ───── */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              height: '85vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                mb: 2,
                p: 1,
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                📄 Document Agent
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: '0.72rem', lineHeight: 1 }}>
                Upload a PDF or image, and the AI will extract text & provide
                insights. <br />
                <strong>Note:</strong> Your document is processed securely. We do not store the actual document or extracted fields in readable form.
                We only retain anonymized vector embeddings used for retrieval-based accuracy.
                These embeddings are not human-readable and cannot be reverse-engineered into
                the original document.
              </Typography>
 
              <Button variant="contained" component="label" sx={{ mb: 1 }}>
                {paidFile ? 'Change File' : 'Select PDF / Image'}
                <input
                  type="file"
                  accept=".pdf,image/*"
                  hidden
                  onChange={handlePaidFileChange}
                />
              </Button>
 
              {paidFile && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Selected: {paidFile.name} ({Math.round(paidFile.size / 1024)} KB)
                </Typography>
              )}
 
              {paidUploadProgress > 0 && (
                <LinearProgress
                  variant="determinate"
                  value={paidUploadProgress}
                  sx={{ mt: 1, mb: 1 }}
                />
              )}
 
              <Button
                variant="contained"
                color="primary"
                onClick={handlePaidSubmit}
                disabled={paidLoading || !paidFile}
                fullWidth
              >
                {paidLoading ? <CircularProgress size={20} /> : 'Review Document'}
              </Button>
 
              {paidErrorMsg && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {paidErrorMsg}
                </Typography>
              )}
            </Paper>
 
            {paidExtractedText && (
              <Paper
                elevation={1}
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: '#fffde7',
                  borderRadius: 2,
                  overflowY: 'auto',
                  maxHeight: '25vh',
                }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 500 }}
                >
                  🔍 Extracted Text
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                >
                  {paidExtractedText}
                </Typography>
              </Paper>
            )}
 
 
        {/* ───── Fixed Bottom Panel: AI Document Analysis (55vh) ───── */}
        <Paper
          elevation={3}
          sx={{
            position: 'static',
            mt: 0,
            bgcolor: '#e3f2fd',
            p: 2,
            pb: 1.5,
            border: '1px solid #cfe3f3',
            borderRadius: 2,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
            🤖 AI Document Analysis
          </Typography>
          {typeof paidAiResponse === 'object' && paidAiResponse !== null ? (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }}>
                🔎 Important Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                We have verified the values of each parameter across the submitted documents, referring to mandatory and recommended requirements under standard export documentation practices. This analysis is subject to the accuracy of OCR (Optical Character Recognition). While a high-quality OCR engine is used, results may vary depending on the scan clarity and layout of the document. We strongly advise you to manually review any Mandatory requirements and Recommendations to ensure complete compliance with banking and customs requirements. As a precaution, we've flagged all verbatim mismatches in soft red within the Excel file. You can download the sheet for detailed examination.
              </Typography>
 
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                📋 Neutral Comments
              </Typography>
              {Array.isArray(paidAiResponse['Analysis']) ? (
                paidAiResponse['Analysis'].map((item, index) => (
                  <Typography key={index} variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    • {item}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {paidAiResponse['Analysis'] || 'Not found'}
                </Typography>
              )}
 
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold' }}>
                🛑 Observations
              </Typography>
              {Array.isArray(paidAiResponse['Red Flags']) ? (
                paidAiResponse['Red Flags'].map((item, index) => (
                  <Typography key={index} variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    • {item}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {paidAiResponse['Red Flags'] || 'Not found'}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {paidAiResponse ||
                'Upload a document and click “Review Document” to see the AI Insights here.'}
            </Typography>
          )}
 
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto', pt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleDownload}
              disabled={!paidFile}
            >
              Download Shipment Document Report
            </Button>
          </Box>
        </Paper>
          </Grid>
        </Grid>
        {/* ───── Classification Confirmation Dialog ───── */}
        <Dialog
          open={showClassificationPopup}
          onClose={() => setShowClassificationPopup(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>📄 Confirm or Update Document Classification</DialogTitle>
          <DialogContent dividers>
            {/* Advanced license (>10 pages) → show range view */}
            {advancedLicense !== 0 && numPages > 10 ? (
              Array.isArray(rangeList) && rangeList.length > 0 ? (
                rangeList.map((r, i) => (
                  <Box key={i} display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <TextField
                      label="Doc Type"
                      variant="outlined"
                      size="small"
                      value={r.doc_type}
                      onChange={(e) => {
                        const updated = [...rangeList];
                        updated[i].doc_type = e.target.value;
                        setRangeList(updated);
                      }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="From"
                      size="small"
                      type="number"
                      value={r.start_page || ""}
                      onChange={(e) => {
                        const updated = [...rangeList];
                        updated[i].start_page = Number(e.target.value);
                        setRangeList(updated);
                      }}
                      sx={{ width: 70 }}
                    />
                    <TextField
                      label="To"
                      size="small"
                      type="number"
                      value={
                        r.end_page !== null && r.end_page !== undefined
                          ? r.end_page
                          : numPages
                      }
                      onChange={(e) => {
                        const updated = [...rangeList];
                        updated[i].end_page = Number(e.target.value);
                        setRangeList(updated);
                      }}
                      sx={{ width: 70 }}
                    />
                  </Box>
                ))
              ) : (
                <Typography>No classification ranges found.</Typography>
              )
            ) : (
              // Basic license → per page view
              Array.isArray(friendlyClassification) && friendlyClassification.length > 0 ? (
                friendlyClassification.map((f, i) => (
                  <Box key={i} display="flex" alignItems="center" gap={2} sx={{ mb: 1 }}>
                    <Typography sx={{ minWidth: 60 }}>Page {f.page}</Typography>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={f.doc_type}
                      onChange={(e) => {
                        const updated = [...friendlyClassification];
                        updated[i].doc_type = e.target.value;
                        setFriendlyClassification(updated);
                      }}
                    />
                  </Box>
                ))
              ) : (
                <Typography>No page-level classification found.</Typography>
              )
            )}
          </DialogContent>
 
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => {
                setShowClassificationPopup(false);
                handleFinalValidation(
                  advancedLicense !== 0 && numPages > 10
                    ? rangeList
                    : friendlyClassification
                );
              }}
            >
              Confirm & Continue
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setShowClassificationPopup(false);
                setPaidLoading(false);
                setPaidErrorMsg('');
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }